import { useRouter } from 'next/router'
import type { KeyedMutator } from 'swr'
import useSWR from 'swr'
import { baseTheme } from 'themes/constants'
import { applyTheme } from 'themes/utils'
import { NotFound } from 'shared/errors/not-found'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser from 'shared/hooks/use-user'
import { getSchoolUrl } from 'modules/school/utils/get-school-url'
import { MEMBERSHIP_COURSE_API } from '../api/membership-api'
import type { CourseDataInterface } from '../types/course-interface'
import { CourseAccessTypeEnum } from '../types/course-interface'
import { useGetCoursePageUrl } from './use-course-path-url'

export const useCoursePathKey = (): string | undefined => {
  const coursePath = useGetCoursePageUrl()
  return coursePath ? `${MEMBERSHIP_COURSE_API}/${coursePath}` : undefined
}

interface UseCourseDataResult {
  courseData: CourseDataInterface | undefined
  mutate: KeyedMutator<CourseDataInterface>
  isValidating: boolean
  isLoading: boolean
}

export function useCourseData(): UseCourseDataResult {
  const router = useRouter()

  const courseDataKey = useCoursePathKey()
  const { user } = useUser()
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, CourseDataInterface>(
    {
      method: RequestMethodsEnum.get,
      afterFetch: courseData => ({
        ...courseData,
        dominantAccessType: courseData.dominantAccessType ?? CourseAccessTypeEnum.NoAccess,
      }),
    },
  )

  const {
    data: courseData,
    mutate,
    isValidating,
    isLoading,
  } = useSWR<CourseDataInterface>(() => user && courseDataKey, fetcher, {
    onError: error => {
      applyTheme(baseTheme)
      if (error instanceof NotFound) {
        router.push(getSchoolUrl())
      }
    },
  })

  return { courseData, mutate, isValidating, isLoading }
}
